import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  Camera,
  Loader2,
  MapPin,
  ScanFace,
  Trophy,
} from "lucide-react";
import api from "lib/api";
import { useI18n } from "i18n";
import CopyLinkButton from "components/public/CopyLinkButton";
import { shareUrl } from "lib/share";
import {
  SEARCH_PAGE_SIZE,
  completeSearchResults,
} from "lib/faceSearch";
import SelfieSearchModal from "components/public/SelfieSearchModal";
import {
  Lightbox,
  PhotoGridSkeleton,
  PhotoTile,
} from "components/public/PhotoGrid";
import SportCategoryChip from "components/SportCategoryChip";
import {
  championshipLabel,
  eventChipLabel,
  eventShareDescription,
  formatEventDates,
  formatEventPlace,
} from "lib/events";
import { usePageMeta } from "lib/meta";

const PAGE_SIZE = 60;

// Distancia antes do fim da grade em que a proxima pagina ja comeca a carregar:
// o visitante nunca chega a ver o fim da lista parado esperando.
const PREFETCH_MARGIN = "800px";

/**
 * Galeria publica de um evento (`/events/:id`).
 *
 * Duas visoes sobre o mesmo acervo:
 * - **todas as fotos**, em rolagem infinita (as paginas vao sendo emendadas na
 *   grade ate o fim do album);
 * - **"encontre suas fotos"**: o visitante envia uma selfie, o backend calcula
 *   a biometria dela e devolve as fotos onde aquele rosto aparece. A selfie nao
 *   e armazenada — serve so para a comparacao.
 *
 * O resultado da busca e compartilhavel: o backend guarda a lista de fotos
 * encontradas (`FaceSearch`) e a URL ganha `?search=<uuid>` — quem abre esse
 * link ve exatamente as mesmas fotos, sem enviar selfie nenhuma.
 */
export default function PublicEventGallery() {
  const { id } = useParams();
  const { t, lang } = useI18n();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [photos, setPhotos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingFirst, setLoadingFirst] = useState(true);
  const [exhausted, setExhausted] = useState(false);
  // Offset da PROXIMA pagina e trava de concorrencia ficam em ref: a rolagem
  // dispara o carregamento fora do ciclo de render e nao pode ler valor velho.
  const nextOffset = useRef(0);
  const fetching = useRef(false);
  const sentinel = useRef(null);
  // Geracao do acervo em tela: trocar de evento a incrementa e a resposta de um
  // pedido antigo, que chegue depois, e descartada em vez de emendar a foto do
  // evento anterior na galeria nova.
  const generation = useRef(0);

  const [matches, setMatches] = useState(null); // null = mostrando o acervo
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [lightbox, setLightbox] = useState(null);
  // Modal da selfie e o erro dela (mostrado dentro do modal).
  const [selfieOpen, setSelfieOpen] = useState(false);
  const [selfieError, setSelfieError] = useState("");

  const [searchParams, setSearchParams] = useSearchParams();
  const searchId = searchParams.get("search");
  // Busca cujo resultado esta (ou esta indo) para a tela. Uma resposta que
  // chegue depois de o visitante trocar de busca ou voltar ao acervo e ignorada.
  const loadedSearch = useRef(null);

  const showingMatches = matches !== null;

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        setMatches(null);
        const res = await api.get(`/api/public/events/${id}/`);
        if (active) setEvent(res.data);
      } catch {
        if (active) setNotFound(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  /** Carrega a proxima pagina e a emenda no que ja esta na grade. */
  const loadMore = useCallback(async () => {
    if (fetching.current) return;
    fetching.current = true;
    const from = nextOffset.current;
    const mine = generation.current;
    if (from === 0) setLoadingFirst(true);
    try {
      const res = await api.get(`/api/public/events/${id}/photos/`, {
        params: { offset: from, limit: PAGE_SIZE },
      });
      if (mine !== generation.current) return;
      const batch = res.data?.results || [];
      const count = res.data?.count || 0;
      setTotal(count);
      setPhotos((prev) => (from === 0 ? batch : [...prev, ...batch]));
      nextOffset.current = from + batch.length;
      // Lote incompleto (ou ja cobrimos o total) = fim do album.
      if (batch.length < PAGE_SIZE || nextOffset.current >= count) {
        setExhausted(true);
      }
    } catch {
      // Para a rolagem infinita no erro: sem isso cada scroll repetiria a
      // chamada que acabou de falhar.
      if (mine === generation.current) setExhausted(true);
    } finally {
      // A resposta obsoleta nao mexe na trava do pedido que esta correndo agora.
      if (mine === generation.current) {
        fetching.current = false;
        setLoadingFirst(false);
      }
    }
  }, [id]);

  useEffect(() => {
    if (notFound) return;
    generation.current += 1;
    nextOffset.current = 0;
    fetching.current = false;
    setPhotos([]);
    setTotal(0);
    setExhausted(false);
    setLoadingFirst(true);
    loadMore();
  }, [loadMore, notFound]);

  // Rolagem infinita: o observador vive enquanto houver pagina a buscar e e
  // recriado a cada lote — um sentinela que continua visivel depois da emenda
  // nao emite novo `isIntersecting`, entao e a recriacao que encadeia as
  // paginas quando a tela e alta demais para uma so.
  useEffect(() => {
    if (loading || notFound || showingMatches || exhausted) return undefined;
    const node = sentinel.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      { rootMargin: PREFETCH_MARGIN }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loading, notFound, showingMatches, exhausted, loadMore, photos.length]);

  // Link compartilhado (`?search=<uuid>`): reabre o resultado guardado. Sem o
  // parametro — "ver todas" ou o botao voltar do navegador — o acervo volta.
  useEffect(() => {
    if (!searchId) {
      if (loadedSearch.current) {
        loadedSearch.current = null;
        setMatches(null);
      }
      return;
    }
    if (searchId === loadedSearch.current) return;
    loadedSearch.current = searchId;
    setSearching(true);
    setSearchError("");
    api
      .get(`/api/public/face-searches/${searchId}/`, {
        params: { limit: SEARCH_PAGE_SIZE },
      })
      .then(async (res) => {
        if (loadedSearch.current !== searchId) return;
        // Link de busca de OUTRO evento nao se mistura com esta galeria.
        if (res.data?.event !== id) throw new Error("search scope");
        const results = await completeSearchResults(res.data);
        if (loadedSearch.current !== searchId) return;
        setMatches(results);
      })
      .catch(() => {
        if (loadedSearch.current !== searchId) return;
        loadedSearch.current = null;
        setMatches(null);
        setSearchError(t("public.search.sharedNotFound"));
        setSearchParams({}, { replace: true });
      })
      .finally(() => setSearching(false));
  }, [searchId, id, t, setSearchParams]);

  // Titulo e meta tags desta galeria no navegador. `share_image_url` e o mesmo
  // cartao que o crawler recebe — a pagina anuncia o que a rede social anuncia.
  usePageMeta(
    event && {
      title: event.name,
      description: eventShareDescription(event, lang),
      image: event.share_image_url,
      type: "website",
    }
  );

  const searchByFace = async (file) => {
    if (!file) return;
    setSearching(true);
    setSearchError("");
    setSelfieError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post(`/api/public/events/${id}/search-faces/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { limit: SEARCH_PAGE_SIZE },
      });
      const data = res.data || {};
      setMatches(await completeSearchResults(data));
      // O id da busca vai para a URL: e ele que o "copiar link" compartilha.
      // Busca sem resultado nao gera link.
      loadedSearch.current = data.search_id || null;
      setSearchParams(data.search_id ? { search: data.search_id } : {});
      setSelfieOpen(false);
    } catch (err) {
      // O modal fica aberto com o motivo, para tentar outra imagem.
      setSelfieError(err.response?.data?.detail || t("public.search.error"));
    } finally {
      setSearching(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
      </div>
    );
  }

  if (notFound || !event) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-4 px-4 py-24 text-center">
        <Camera size={36} className="text-neutral-300" />
        <h1 className="text-xl font-semibold text-neutral-900">
          {t("public.event.notFound")}
        </h1>
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-600"
        >
          <ArrowLeft size={16} />
          {t("public.event.backToEvents")}
        </Link>
      </div>
    );
  }

  const place = formatEventPlace(event);
  const visible = matches ?? photos;

  return (
    <>
      {/* ------------------------------ capa ------------------------------ */}
      <section className="relative border-b border-neutral-200 bg-neutral-900">
        {event.cover_url && (
          <img
            src={event.cover_url}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover opacity-30"
          />
        )}
        <div className="relative mx-auto w-full max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-white/80 transition-colors hover:text-white"
          >
            <ArrowLeft size={16} />
            {t("public.event.backToEvents")}
          </Link>

          {/* Etapa de um campeonato: o nome da competicao vira o caminho de
              volta para as demais etapas. `flex w-fit` (e nao `inline-flex`)
              para o link ocupar uma LINHA PROPRIA acima do titulo — inline ele
              subia para o lado do "voltar aos eventos". */}
          {event.championship && (
            <Link
              to={`/championships/${event.championship.slug}`}
              className="mt-5 flex w-fit max-w-full items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-amber-300 transition-colors hover:text-amber-200"
            >
              <Trophy size={14} className="shrink-0" />
              <span className="truncate">{championshipLabel(event.championship)}</span>
            </Link>
          )}

          <h1 className="mt-2 max-w-3xl text-3xl font-bold tracking-tight text-white sm:text-4xl">
            {event.name}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/80">
            <span className="flex items-center gap-1.5">
              <CalendarDays size={15} />
              {formatEventDates(event, lang)}
            </span>
            {place && (
              <span className="flex items-center gap-1.5">
                <MapPin size={15} />
                {event.location_name ? `${event.location_name} — ${place}` : place}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Camera size={15} />
              {event.photo_count} {t("public.event.photos")}
            </span>
            <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold">
              {eventChipLabel(t, event)}
            </span>
            <SportCategoryChip chip={event.sport_category_chip} />
          </div>

          {event.description && (
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/70">
              {event.description}
            </p>
          )}
        </div>
      </section>

      {/* ---------------------- encontre suas fotos ----------------------- */}
      <section className="border-b border-neutral-200 bg-amber-50">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 px-4 py-8 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-neutral-900">
              <ScanFace size={20} className="text-amber-600" />
              {t("public.search.title")}
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-neutral-600">
              {t("public.search.hint")}
            </p>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-3">
            {showingMatches && searchId && (
              <CopyLinkButton
                url={() => shareUrl(`/events/${id}`, { search: searchId })}
                label={t("public.search.copyLink")}
              />
            )}
            {showingMatches && (
              <button
                type="button"
                onClick={() => {
                  loadedSearch.current = null;
                  setMatches(null);
                  setSearchError("");
                  setSearchParams({});
                }}
                className="rounded-full border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
              >
                {t("public.search.showAll")}
              </button>
            )}
            <button
              type="button"
              disabled={searching}
              onClick={() => {
                  setSelfieError("");
                  setSelfieOpen(true);
                }}
              className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-600 disabled:opacity-60"
            >
              {searching ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <ScanFace size={16} />
              )}
              {t("public.search.action")}
            </button>
          </div>
        </div>

        {searchError && (
          <div className="mx-auto w-full max-w-7xl px-4 pb-6 sm:px-6">
            <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {searchError}
            </p>
          </div>
        )}
      </section>

      {/* ----------------------------- fotos ------------------------------ */}
      <section className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6">
        <div className="mb-5 flex items-baseline justify-between gap-4">
          <h2 className="text-lg font-semibold text-neutral-900">
            {showingMatches
              ? t("public.search.resultsTitle")
              : t("public.event.allPhotos")}
            {/* Fora da busca por rosto o acervo chega aos poucos: enquanto
                houver pagina a carregar, o contador mostra quanto do album ja
                esta na tela. */}
            <span className="ml-2 text-sm font-normal text-neutral-500">
              {showingMatches || exhausted
                ? visible.length
                : loadingFirst
                ? ""
                : `${visible.length} / ${total}`}
            </span>
          </h2>
        </div>

        {loadingFirst && !showingMatches ? (
          <PhotoGridSkeleton />
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 px-6 py-20 text-center">
            <Camera size={30} className="text-neutral-300" />
            <p className="font-semibold text-neutral-800">
              {showingMatches
                ? t("public.search.noResults")
                : t("public.event.noPhotos")}
            </p>
            <p className="max-w-md text-sm text-neutral-500">
              {showingMatches
                ? t("public.search.noResultsHint")
                : t("public.event.noPhotosHint")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {visible.map((photo, index) => (
              <PhotoTile
                key={photo.id}
                photo={photo}
                onOpen={() => setLightbox(index)}
                t={t}
              />
            ))}
          </div>
        )}

        {/* Sentinela da rolagem infinita: entrou no campo de visao (com a
            margem de prefetch), a proxima pagina e buscada. Fica fora do fluxo
            de busca por rosto, que devolve o resultado inteiro de uma vez. */}
        {!showingMatches && !exhausted && (
          <div
            ref={sentinel}
            className="mt-8 flex items-center justify-center gap-2 py-6 text-sm text-neutral-500"
          >
            {/* Na primeira pagina quem sinaliza a espera e o esqueleto da
                grade — o sentinela fica montado, mas calado. */}
            {!loadingFirst && (
              <>
                <Loader2 size={16} className="animate-spin text-amber-500" />
                {t("public.event.loadingMore")}
              </>
            )}
          </div>
        )}

        {!showingMatches && exhausted && visible.length > PAGE_SIZE && (
          <p className="mt-8 py-6 text-center text-sm text-neutral-400">
            {t("public.event.endOfAlbum")}
          </p>
        )}
      </section>

      {lightbox !== null && visible[lightbox] && (
        <Lightbox
          photos={visible}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onNavigate={setLightbox}
          hasMore={!showingMatches && !exhausted}
          onNeedMore={loadMore}
          t={t}
        />
      )}

      <SelfieSearchModal
        open={selfieOpen}
        onClose={() => setSelfieOpen(false)}
        onSelect={searchByFace}
        busy={searching}
        error={selfieError}
      />
    </>
  );
}
